import {Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';

import {Store} from '@ngrx/store';

import {Observable, Subscription} from 'rxjs';

import {ToastrService} from 'ngx-toastr';

import * as moment from 'moment';

import {AppState} from 'src/app/state';
import {Language} from 'src/app/models/language';
import {InterventionContentItem} from 'src/app/models/intervention-content-item';
import {Module} from 'src/app/models/module';
import {ModuleItem} from 'src/app/models/module-item';
import {CalculatedVariable} from 'src/app/models/calculated-variable';
import {ESMTriggerRule, Trigger, TriggerCardview, TriggerRule} from 'src/app/models/trigger';
import {ModuleItemAvailableType} from 'src/app/models/module-item-type';
import {ModuleItemAvailableSubType} from 'src/app/models/module-item-sub-type';
import {ModuleItemAvailableAttribute} from 'src/app/models/module-item-attribute';
import {ExpressionOperators} from 'src/app/models/expression-operators';
import {
  API_DATE_FORMAT,
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
  OPEN_TIMING_RULES,
  CLOSING_TIMING_RULES, ESM_TIMING_RULES
} from 'src/app/core/dashboard-constants';
import {TIMER_ARRAY_UNITS} from '../../helpers/time-array-helper';
import {getStringChoices, getNumberChoices} from '../../helpers/module-item-helper';
import {PrivilegeService} from 'src/app/dashboard/services/privilege/privilege.service';
import {DashboardStripHtmlPipe} from 'src/app/core/pipes/dashboard-strip-html/dashboard-strip-html.pipe';
import {selectCalculatedVariables} from 'src/app/dashboard/state/calculated-variable/calculated-variable.selectors';
import {
  selectIsLoadingModules,
  selectModulesInSelectedIntervention,
  selectModulesLoaded,
} from 'src/app/dashboard/state/module/module.selectors';
import {selectSelectedLanguage} from 'src/app/dashboard/state/study/study.selectors';
import {updateModule} from "../../../state/module/module.actions";
import {saveTimingRules} from 'src/app/dashboard/state/timing-rules/timing-rules.actions';
import {addTimingRuleToContentItem} from "../../../state/intervention-content-item/intervention-content-item.actions";
import {NotificationComponent} from "../notification/notification.component";
import {ESMCalenderComponent} from "../esm-calender/esm-calender.component";
import {
  CanEditTimingRulesDialogComponent
} from "../can-edit-timing-rules-dialog/can-edit-timing-rules-dialog.component";
import {TimingRulesService} from "../../../services/timing-rules/timing-rules.service";

const RELATIVE_FROM_INTERVENTION = 'relative_from_intervention';
const RELATIVE_FROM_MODULE = 'relative_from_module';
const UNFINISHED_MODULE = 'unfinished_module';
const BASED_ON_ANSWER = 'based_on_answer';
const FIXED_DATE = 'fixed_date';
const CALCULATED_VARIABLE = 'calculated_variable';
const RELATIVE_AFTER_MODULE = 'relative_after_module';
const ESM_FIXED_DATE = 'esm_fixed_date';
const ESM_RELATIVE_DATE = 'esm_relative_date';

@Component({
  selector: 'dashboard-timing-rules-dialog',
  templateUrl: './timing-rules-dialog.component.html',
  styleUrls: ['./timing-rules-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TimingRulesDialogComponent implements OnInit, OnDestroy {
  @ViewChild(NotificationComponent) notificationCmp: NotificationComponent;
  @ViewChild(ESMCalenderComponent) esmCalenderComponent: ESMCalenderComponent;

  isLoadingModules$: Observable<boolean>;
  modulesLoaded$: Observable<boolean>;

  modules: Module[];
  availableModules: Module[];
  calculatedVariables: CalculatedVariable[];
  selectedLanguage: Language;
  moduleItems = [];

  contentItem: InterventionContentItem;
  interventionContentItems: InterventionContentItem[];
  moduleName: string;

  triggers: Trigger[] = [];
  expandTriggerCards: TriggerCardview[] = [];

  calculatedVariablesOperators = [];
  ruleOptions = [];

  triggerRuleIndex = 0;

  now = moment(new Date());

  TIMER_ARRAY_UNITS = TIMER_ARRAY_UNITS;
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  OPEN_TIMING_RULES = OPEN_TIMING_RULES;
  CLOSING_TIMING_RULES = CLOSING_TIMING_RULES;
  ESM_TIMING_RULES = ESM_TIMING_RULES;

  ExpressionOperators = ExpressionOperators;
  ModuleItemAvailableType = ModuleItemAvailableType;

  errors = [];

  private subscription = new Subscription();

  hasRelativeAfterModule = false;

  esmFixedRules: ESMTriggerRule[] = [];
  esmRelativeRules: ESMTriggerRule[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { contentItem: InterventionContentItem, interventionContentItems: InterventionContentItem[]},
    public store: Store<AppState>,
    public dashboardStripHtmlPipe: DashboardStripHtmlPipe,
    private toastrService: ToastrService,
    public privilegeService: PrivilegeService,
    private dialog: MatDialog,
    private timingRulesService: TimingRulesService
  ) {
  }

  ngOnInit(): void {
    this.moduleName = this.data?.contentItem?.module?.name;
    this.contentItem = this.data?.contentItem;
    this.interventionContentItems = this.data?.interventionContentItems;

    const modulesWithESM = this.interventionContentItems?.filter(contentItem => contentItem.triggers && contentItem.triggers.length > 0 && contentItem.triggers[0].esmRules?.length > 0);

    this.isLoadingModules$ = this.store.select(selectIsLoadingModules);
    this.modulesLoaded$ = this.store.select(selectModulesLoaded);

    this.subscription.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => (this.selectedLanguage = language))
    );

    this.subscription.add(
      this.store
        .select(selectCalculatedVariables)
        .subscribe((calculatedVariables) => (this.calculatedVariables = calculatedVariables))
    );

    this.subscription.add(
      this.store.select(selectModulesInSelectedIntervention).subscribe((modules) => {
        this.modules = modules;
        this.availableModules = modules.slice().filter((module) => module.id !== this.contentItem?.moduleId);

        // remove the modules that have esm triggers
        const idsToRemove = modulesWithESM.map(contentItem => contentItem.module.id);
        this.availableModules = this.availableModules.filter(module => {
          return !idsToRemove.includes(module.id);
        });

        this.fillModuleItems();
        this.initRules();
      })
    );

    if (this.contentItem.triggers) {
      this.initTriggers();
    }

    if (this.contentItem.module.removeAfter) {
      this.initRelativeAfterIntervention();
    }
  }

  initRelativeAfterIntervention(): void {
    if (!this.triggers || this.triggers.length === 0) {
      const trigger: Trigger = {
        interventionModulesId: this.contentItem.id,
        rules: [],
        operator: '&&',
      };

      const triggerRule: TriggerRule = {
        triggerId: trigger.id,
        moduleId: this.contentItem.module.id,
        moduleItemId: 0,
        calculatedVariableId: 0,
        triggerType: RELATIVE_AFTER_MODULE,
        relativeOffset: this.contentItem.module.removeAfter,
        operator: ExpressionOperators.EQUAL_TO.operator,
        disable: 1,
      };

      trigger.rules.push(triggerRule);

      this.expandTriggerCards.push({
        expand: true,
        trigger
      })
      this.triggers.push(trigger);
    }
  }

  initTriggers(): void {
    this.triggers = JSON.parse(JSON.stringify(this.contentItem.triggers));
    let checked = false;
    this.triggers.forEach((trigger) => {
      trigger.rules?.forEach((rule) => {
        if (rule.relativeOffset) {
          this.fillRelativeOffsets(rule);
        }
      });
      this.expandTriggerCards.push({
        expand: true,
        trigger
      });
      if (trigger.esmRules.length > 0 && !checked) {
        const esmRule = trigger.esmRules[0];
        if (trigger.rules.length < 1) {
          trigger.rules[0] = {
            id: esmRule.id,
            triggerId: esmRule.triggerId,
            triggerType: esmRule.triggerType,
            disable: 2
          };
        }
        this.expandTriggerCards.push({
          expand: true,
          trigger
        });
        checked = true;
      }
    });
  }

  ruleTypeChange(rules, value): void {
    rules.forEach(rule => {
      rule.triggerType = '';
      rule.disable = value
    });
    this.triggerRuleIndex = value;
  }

  fillModuleItems(): void {
    this.modules.forEach((module) => {
      const itemsPerModule = [];
      module.moduleItems.forEach((moduleItem) => {
        const item = {
          id: moduleItem.id,
          sortOrder: moduleItem.sortOrder,
          label: this.dashboardStripHtmlPipe.transform(this.getModuleItemLabel(moduleItem)),
        };
        if (moduleItem.type === ModuleItemAvailableType.NUMBER || moduleItem.type === ModuleItemAvailableType.TEXT) {
          itemsPerModule.push(item);
        }
      });
      this.moduleItems[module.id] = itemsPerModule;
    });
  }

  initRelativeOffsets(rule: TriggerRule): void {
    if (rule.triggerType === RELATIVE_FROM_INTERVENTION ||
      rule.triggerType === RELATIVE_FROM_MODULE ||
      rule.triggerType === RELATIVE_AFTER_MODULE) {
      this.fillRelativeOffsets(rule);
    }
  }

  fillRelativeOffsets(rule: TriggerRule): void {
    rule.weeks = rule.relativeOffset ? Math.floor(rule.relativeOffset / (7 * 24 * 60)) : 0;
    rule.days = rule.relativeOffset ? Math.floor((rule.relativeOffset % (7 * 24 * 60)) / (24 * 60)) : 0;
    rule.hours = rule.relativeOffset ? Math.floor((rule.relativeOffset % (24 * 60)) / 60) : 0;
    rule.minutes = rule.relativeOffset ? Math.floor(rule.relativeOffset % 60) : 0;
  }

  getModuleItemLabel(moduleItem): string {
    const filteredModuleItemsTranslations = moduleItem.translations?.filter(
      (translation) => translation.identifier === 'question'
    );
    return filteredModuleItemsTranslations.length
      ? filteredModuleItemsTranslations[0].translation
      : 'Untitled module item';
  }

  enableTimingRules(): void {
    this.addTrigger();
  }

  initRules() {
    this.triggers?.forEach((trigger) => {
      trigger.rules?.forEach((rule) => {
        if (rule.triggerType === BASED_ON_ANSWER) {
          this.initRuleOptions(rule);
        } else if (rule.triggerType === CALCULATED_VARIABLE) {
          this.initCalculatedVariableOptions(rule);
        } else if (rule.triggerType === RELATIVE_FROM_INTERVENTION ||
          rule.triggerType === RELATIVE_FROM_MODULE ||
          rule.triggerType === RELATIVE_AFTER_MODULE) {
          this.fillRelativeOffsets(rule);
        } else if (rule.triggerType === ESM_FIXED_DATE ||
          rule.triggerType === ESM_RELATIVE_DATE) {
          this.initESMRules(trigger.esmRules)
        }
      });
    });
  }

  initRuleOptions(rule: TriggerRule): void {
    const module: Module = this.getModuleById(rule.moduleId);
    const moduleItem: ModuleItem = this.getModuleItemById(module, rule.moduleItemId);
    if (moduleItem) {
      const answerOptions = this.getAnswerOptions(moduleItem);

      this.ruleOptions[rule.moduleItemId] = {
        operators: this.getOperators(moduleItem),
        answerOptions,
        responseType:
          moduleItem.type === ModuleItemAvailableType.NUMBER
            ? ModuleItemAvailableType.NUMBER
            : ModuleItemAvailableType.INDEX,
      };

      if (!rule.operator) {
        rule.operator = this.ExpressionOperators.EQUAL_TO.operator;
      }
      if (answerOptions && !rule.answer) {
        rule.answer = answerOptions[0];
      }
    }
  }

  initESMRules(esmRules: ESMTriggerRule[]) {
    if (esmRules[0].triggerType === ESM_FIXED_DATE) {
      this.esmFixedRules = esmRules
    } else if (esmRules[0].triggerType === ESM_RELATIVE_DATE) {
      this.esmRelativeRules = esmRules
    }
  }

  getModuleById(moduleId: number): Module {
    return this.modules?.find((module) => module.id === moduleId);
  }

  getModuleItemById(module: Module, moduleItemId: number): ModuleItem {
    return module?.moduleItems?.find((moduleItem) => moduleItem.id === moduleItemId);
  }

  getAnswerOptions(moduleItem: ModuleItem) {
    if (moduleItem.subType === ModuleItemAvailableSubType.YES_NO) {
      return ['no', 'yes'];
    } else if (
      moduleItem.subType === ModuleItemAvailableSubType.MULTIPLE_CHOICE ||
      moduleItem.subType === ModuleItemAvailableSubType.LIKERT ||
      (moduleItem.subType === ModuleItemAvailableSubType.DROPDOWN && moduleItem.type === ModuleItemAvailableType.TEXT)
    ) {
      return getStringChoices(moduleItem, this.selectedLanguage);
    } else if (
      (moduleItem.subType === ModuleItemAvailableSubType.DROPDOWN &&
        moduleItem.type === ModuleItemAvailableType.NUMBER) ||
      moduleItem.subType === ModuleItemAvailableSubType.SINGLE_CHOICE ||
      moduleItem.subType === ModuleItemAvailableSubType.SLIDER
    ) {
      const minValueAttribute = parseInt(
        moduleItem.attributes?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.MIN_VALUE)
          .value,
        10
      );
      const maxValueAttribute = parseInt(
        moduleItem.attributes?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.MAX_VALUE)
          .value,
        10
      );
      const stepSizeValueAttribute = parseInt(
        moduleItem.attributes?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.STEP_SIZE)
          .value,
        10
      );
      return getNumberChoices(minValueAttribute, maxValueAttribute, stepSizeValueAttribute);
    }
    return [];
  }

  getOperators(moduleItem: ModuleItem) {
    let operators = [ExpressionOperators.EQUAL_TO, ExpressionOperators.NOT_EQUAL_TO];

    if (moduleItem.type === ModuleItemAvailableType.NUMBER) {
      operators = [
        ...operators,
        ...[
          ExpressionOperators.GREATER_THAN,
          ExpressionOperators.LESS_THAN,
          ExpressionOperators.GREATER_THAN_OR_EQUAL_TO,
          ExpressionOperators.LESS_THAN_OR_EQUAL_TO,
        ],
      ];
    }

    return operators;
  }

  initCalculatedVariableOptions(rule: TriggerRule): void {
    this.calculatedVariablesOperators = this.getCalculatedVariablesOperators();

    if (!rule.operator) {
      rule.operator = ExpressionOperators.EQUAL_TO.operator;
    }
  }

  getCalculatedVariablesOperators() {
    return [
      ExpressionOperators.EQUAL_TO,
      ExpressionOperators.NOT_EQUAL_TO,
      ExpressionOperators.GREATER_THAN,
      ExpressionOperators.LESS_THAN,
      ExpressionOperators.GREATER_THAN_OR_EQUAL_TO,
      ExpressionOperators.LESS_THAN_OR_EQUAL_TO,
    ];
  }

  addTrigger(): void {
    const trigger: Trigger = {
      interventionModulesId: this.contentItem.id,
      rules: [],
      operator: '&&',
    };

    this.addTriggerRule(trigger);
    this.expandTriggerCards.push({
      expand: true,
      trigger
    });
    this.triggers.push(trigger);
  }

  removeTrigger(triggerIndex: number): void {
    this.timingRulesService.esmTriggerEditable(
      this.contentItem?.module?.studyId,
      this.contentItem?.interventionId,
      this.contentItem?.module?.id).subscribe(result => {
      if (result) {
        this.triggers.splice(triggerIndex, 1);
      } else {
        this.dialog.open(CanEditTimingRulesDialogComponent, {
          width: '250px',
        });
      }
    });
  }

  addTriggerRule(trigger: Trigger): void {
    const triggerRule: TriggerRule = {
      triggerId: trigger.id,
      moduleId: 0,
      moduleItemId: 0,
      calculatedVariableId: 0,
      triggerType: '',
      relativeOffset: 0,
      operator: ExpressionOperators.EQUAL_TO.operator,
      disable: trigger.rules[0] ? trigger.rules[0].disable : 0,
    };

    trigger.rules.push(triggerRule);
  }

  removeTriggerRule(trigger: Trigger, triggerRuleIndex: number): void {
    trigger.rules.splice(triggerRuleIndex, 1);
  }

  validate(): void {
    this.errors = [];
    this.triggers.forEach((trigger, triggerIndex) => {
      trigger.rules.forEach((rule, ruleIndex) => {
        if (!rule.triggerType) {
          this.errors[triggerIndex] = "You haven't selected a trigger type";
        } else if ((rule.triggerType === RELATIVE_FROM_INTERVENTION || rule.triggerType === RELATIVE_FROM_MODULE) &&
          !rule.relativeOffset) {
          this.errors[triggerIndex] = "You haven't set an offset";
        } else if (rule.triggerType === RELATIVE_AFTER_MODULE && !rule.relativeOffset) {
          this.errors[triggerIndex] = "You haven't set a time"
        } else if (
          (rule.triggerType === RELATIVE_FROM_MODULE || rule.triggerType === UNFINISHED_MODULE) &&
          (!rule.moduleId || rule.moduleId <= 0)
        ) {
          this.errors[triggerIndex] = "You haven't selected a module";
        } else if (rule.triggerType === BASED_ON_ANSWER) {
          if (!rule.moduleItemId || rule.moduleItemId <= 0) {
            this.errors[triggerIndex] = "You haven't selected a module item";
          } else if (!rule.answer) {
            this.errors[triggerIndex] = "You haven't entered an answer for the module item";
          }
        } else if (rule.triggerType === FIXED_DATE && !rule.fixedDate) {
          this.errors[triggerIndex] = "You haven't entered a fixed date";
        } else if (rule.triggerType === CALCULATED_VARIABLE) {
          if (!rule.calculatedVariableId) {
            this.errors[triggerIndex] = "You haven't selected a calculated variable";
          } else if (!rule.answer) {
            this.errors[triggerIndex] = "You haven't entered a value for comparision";
          }
        } else if (rule.triggerType === ESM_FIXED_DATE || rule.triggerType === ESM_RELATIVE_DATE) {
          if (this.esmCalenderComponent.getESMTriggerRules().length < 1) {
            this.errors[triggerIndex] = "You can not save a empty calender, please schedule a time for the module";
          }
        }

        trigger.rules.forEach((ruleToCompare, ruleToCompareIndex) => {
          if (ruleIndex === ruleToCompareIndex) {
            return;
          }
          if (
            rule.triggerType === RELATIVE_FROM_INTERVENTION &&
            ruleToCompare.triggerType === RELATIVE_FROM_INTERVENTION
          ) {
            this.errors[triggerIndex] = 'You can only have one trigger relative from the intervention start';
          } else if (
            rule.triggerType === RELATIVE_AFTER_MODULE &&
            ruleToCompare.triggerType === RELATIVE_AFTER_MODULE
          ) {
            this.errors[triggerIndex] = 'You can only have one trigger relative after the intervention start';
          } else if (
            rule.triggerType === RELATIVE_FROM_MODULE &&
            ruleToCompare.triggerType === RELATIVE_FROM_MODULE &&
            rule.moduleId === ruleToCompare.moduleId
          ) {
            this.errors[triggerIndex] = 'You can only have one trigger relative from the same module';
          } else if (rule.triggerType === FIXED_DATE && ruleToCompare.triggerType === FIXED_DATE) {
            this.errors[triggerIndex] = 'You can only have one trigger rule on a fixed date';
          }
        });
      });
    });
  }

  saveTest() {
    console.log(this.esmCalenderComponent.getESMTriggerRules());
  }

  saveTimingRules(): void {
    this.notificationCmp.saveNotifications();
    this.triggers.forEach((trigger) => {
      trigger.rules.forEach((rule) => {
        if (rule.triggerType === RELATIVE_FROM_INTERVENTION ||
          rule.triggerType === RELATIVE_FROM_MODULE ||
          rule.triggerType === RELATIVE_AFTER_MODULE) {
          rule.relativeOffset = rule.weeks * 7 * 24 * 60 + rule.days * 24 * 60 + rule.hours * 60 + rule.minutes;
        }
      });
    });

    this.validate();

    if (this.errors.length) {
      this.toastrService.warning('Please fix the errors before saving the triggers');
      return;
    }

    if (this.notificationCmp.notificationsHasErrors()) {
      this.toastrService.warning('Please fix the errors before saving the notifications');
      return;
    }

    let relativeAfterModuleIndex;
    this.triggers.forEach((trigger) => {
      trigger.rules.forEach((rule) => {
        if (rule.triggerType === RELATIVE_AFTER_MODULE) {
          if (rule.relativeOffset !== this.contentItem.module.removeAfter) {
            this.saveTemporaryModule(rule.relativeOffset);
            relativeAfterModuleIndex = trigger.rules.indexOf(rule, 0);
            this.hasRelativeAfterModule = true;
          }
        }
        if (rule.triggerType !== CALCULATED_VARIABLE) {
          delete rule.calculatedVariableId;
        }
        if (rule.triggerType === FIXED_DATE) {
          rule.fixedDate = moment(rule.fixedDate).format(API_DATE_FORMAT);
        }
        delete rule.weeks;
        delete rule.days;
        delete rule.hours;
        delete rule.minutes;
      });
    });

    if (relativeAfterModuleIndex > -1) {
      this.triggers.forEach((trigger) => {
        trigger.rules.forEach((rule) => {
          trigger.rules.splice(relativeAfterModuleIndex, 1);
        });
        if (trigger.rules.length === 0) {
          const triggerIndex = this.triggers.indexOf(trigger, 0);
          if (triggerIndex > -1) {
            this.triggers.splice(triggerIndex, 1)
          }
        }
      });
    }

    if (this.triggers.length > 0) {
      if (this.triggers[0].rules[0].triggerType === ESM_FIXED_DATE ||
        this.triggers[0].rules[0].triggerType === ESM_RELATIVE_DATE) {
        this.triggers[0].esmRules = this.esmCalenderComponent.getESMTriggerRules();
      }
    }

    this.store.dispatch(
      saveTimingRules({
        studyId: this.contentItem?.module?.studyId,
        interventionId: this.contentItem?.interventionId,
        moduleId: this.contentItem?.module?.id,
        triggers: this.triggers,
      })
    );

    this.contentItem = {
      ...this.contentItem,
      triggers: this.triggers,
    };

    this.store.dispatch(addTimingRuleToContentItem({updatedContentItem: this.contentItem}));

    if (!this.hasRelativeAfterModule) {
      this.checkTemporaryModule();
    }
  }

  checkTemporaryModule() {
    let hasTemporaryModuleRule = false;
    this.triggers.forEach((trigger) => {
      trigger.rules.forEach((rule) => {
        if (rule.triggerType === RELATIVE_AFTER_MODULE) {
          if (!hasTemporaryModuleRule) {
            hasTemporaryModuleRule = true;
          }
        }
      });
    });

    if (!hasTemporaryModuleRule) {
      this.saveTemporaryModule(0);
    }
  }

  saveTemporaryModule(time: number) {
    const moduleDetails: Module = this.contentItem?.module;
    moduleDetails.removeAfter = time;

    this.store.dispatch(updateModule({module: moduleDetails}));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  expandTrigger(trigger: Trigger) {
    this.expandTriggerCards.forEach(expandTrigger => {
      if (expandTrigger.trigger === trigger) {
        expandTrigger.expand = !expandTrigger.expand
      }
    });
  }

  getExpandTrigger(trigger: Trigger): boolean {
    let expand: boolean;
    this.expandTriggerCards.forEach(expandTrigger => {
      if (expandTrigger.trigger === trigger) {
        expand = expandTrigger.expand
      }
    });
    return expand;
  }
}
